package ru.chm.gradereport.ui.screens.course

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.CourseAnalyticsDto
import ru.chm.gradereport.model.CourseDto
import ru.chm.gradereport.model.CourseFormsSettingsDto
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.SetAttendanceRequest
import ru.chm.gradereport.model.SetGradeRequest
import ru.chm.gradereport.model.StudentStatsDto
import ru.chm.gradereport.platform.copyTextToClipboard
import ru.chm.gradereport.platform.getApiBaseUrl
import ru.chm.gradereport.ui.components.ErrorBanner
import ru.chm.gradereport.ui.components.HeaderAction
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.ScreenHeader
import ru.chm.gradereport.ui.components.TabChip
import ru.chm.gradereport.ui.components.adaptiveContentPadding
import ru.chm.gradereport.ui.screens.course.sheet.CheckInQrDialog
import ru.chm.gradereport.ui.screens.course.sheet.GradeSheetTable

internal fun courseStudentsJsonUrl(publicToken: String): String =
    getApiBaseUrl().trimEnd('/') + "/api/public/$publicToken/students"

internal fun homeworkSubmissionApiUrl(publicToken: String, homeworkId: String): String =
    getApiBaseUrl().trimEnd('/') + "/api/public/$publicToken/homeworks/$homeworkId/submissions"

/** Build student-facing form URL from admin base setting + form uuid. */
internal fun joinFormsBaseUrl(baseUrl: String, formUuid: String): String {
    val base = baseUrl.trim()
    val uuid = formUuid.trim()
    if (base.isEmpty() || uuid.isEmpty()) return ""
    return when {
        base.contains("{uuid}", ignoreCase = true) ->
            base.replace("{uuid}", uuid, ignoreCase = true)
        base.endsWith('/') -> base + uuid.trimStart('/')
        else -> "$base/$uuid"
    }
}

private enum class CourseTab { Sheet, Students, Analytics }

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun CourseScreen(
    api: GradeApi,
    course: CourseDto,
    editable: Boolean,
    isAdmin: Boolean = false,
    onOpenPublic: (String) -> Unit,
    onOpenPublicHomework: (String) -> Unit = {},
    onOpenPublicStudent: (publicToken: String, studentUid: String) -> Unit = { _, _ -> },
) {
    var tab by remember { mutableStateOf(CourseTab.Sheet) }
    var sheet by remember { mutableStateOf<GradeSheetDto?>(null) }
    var analytics by remember { mutableStateOf<CourseAnalyticsDto?>(null) }
    var stats by remember { mutableStateOf<StudentStatsDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var copiedStudentsJson by remember { mutableStateOf(false) }
    var formsSettings by remember { mutableStateOf(CourseFormsSettingsDto()) }
    var showFormsSettings by remember { mutableStateOf(false) }
    var savingFormsSettings by remember { mutableStateOf(false) }
    var showCheckInQr by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    fun reload(showSpinner: Boolean = true) {
        scope.launch {
            if (showSpinner) loading = true
            error = null
            try {
                sheet = api.getSheet(course.id)
                if (isAdmin) {
                    runCatching { api.getCourseFormsSettings(course.id) }.onSuccess {
                        formsSettings = it
                    }
                }
                if (tab == CourseTab.Analytics) {
                    analytics = api.getAnalytics(course.id)
                }
            } catch (e: Exception) {
                error = e.message
            } finally {
                if (showSpinner) loading = false
            }
        }
    }

    LaunchedEffect(course.id) { reload() }
    LaunchedEffect(tab) {
        if (tab == CourseTab.Analytics) {
            runCatching { analytics = api.getAnalytics(course.id) }
                .onFailure { error = it.message }
        }
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val contentPad = adaptiveContentPadding(maxWidth)
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = contentPad, vertical = 12.dp),
        ) {
            ScreenHeader(
                title = course.name,
                subtitle = course.description.ifBlank { "Учебная ведомость" },
                actions = buildList {
                    if (isAdmin) {
                        add(HeaderAction("Формы", onClick = { showFormsSettings = true }))
                    }
                    if (isAdmin && course.publicToken.isNotBlank()) {
                        add(
                            HeaderAction(
                                label = if (copiedStudentsJson) "Скопировано" else "JSON",
                                onClick = {
                                    copyTextToClipboard(courseStudentsJsonUrl(course.publicToken))
                                    copiedStudentsJson = true
                                },
                            ),
                        )
                    }
                    add(HeaderAction("Публичная", onClick = { onOpenPublic(course.publicToken) }))
                    add(
                        HeaderAction(
                            "ДЗ",
                            onClick = { onOpenPublicHomework(course.homeworkToken.ifBlank { course.publicToken }) },
                        ),
                    )
                },
            )

            Column(modifier = Modifier.weight(1f).fillMaxWidth().fillMaxSize()) {
                    ErrorBanner(error) { error = null }

                    FlowRow(
                        modifier = Modifier.padding(bottom = 10.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        TabChip(text = "Ведомость", selected = tab == CourseTab.Sheet) {
                            tab = CourseTab.Sheet
                        }
                        TabChip(text = "Студенты", selected = tab == CourseTab.Students) {
                            tab = CourseTab.Students
                        }
                        TabChip(text = "Аналитика", selected = tab == CourseTab.Analytics) {
                            tab = CourseTab.Analytics
                        }
                    }

                    when {
                        loading && sheet == null -> LoadingBox()
                        sheet == null -> MutedText("Не удалось загрузить ведомость")
                        else -> {
                            val current = sheet!!
                            when (tab) {
                                CourseTab.Sheet -> Column(
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxWidth()
                                        .verticalScroll(rememberScrollState()),
                                ) {
                                    GradeSheetTable(
                                sheet = current,
                                editable = editable,
                                isAdmin = isAdmin,
                                formsBearerConfigured = formsSettings.formsBearerConfigured,
                                modifier = Modifier.fillMaxWidth(),
                                onCycleAttendance = { studentId, sessionId, status ->
                                    scope.launch {
                                        try {
                                            api.setAttendance(
                                                course.id,
                                                SetAttendanceRequest(studentId, sessionId, status),
                                            )
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onSetGrade = { studentId, homeworkId, score ->
                                    scope.launch {
                                        try {
                                            api.setGrade(course.id, SetGradeRequest(studentId, homeworkId, score))
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onOpenStats = { studentId ->
                                    scope.launch {
                                        try {
                                            stats = api.getStudentStats(course.id, studentId)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onAddSession = { date, title, description ->
                                    scope.launch {
                                        try {
                                            api.addSession(course.id, date, title, description)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onUpdateSession = { sessionId, date, title, description ->
                                    scope.launch {
                                        try {
                                            api.updateSession(course.id, sessionId, date, title, description)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onDeleteSession = { sessionId ->
                                    scope.launch {
                                        try {
                                            api.deleteSession(course.id, sessionId)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onAddHomework = { sessionId, title, description, max ->
                                    scope.launch {
                                        try {
                                            api.addHomework(course.id, title, description, max, sessionId)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onUpdateHomework = { hwId, sessionId, title, description, max ->
                                    scope.launch {
                                        try {
                                            api.updateHomework(
                                                course.id,
                                                hwId,
                                                title,
                                                description,
                                                max,
                                                sessionId,
                                            )
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onDeleteHomework = { hwId ->
                                    scope.launch {
                                        try {
                                            api.deleteHomework(course.id, hwId)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onUpdateSubmission = { submissionId, url ->
                                    scope.launch {
                                        try {
                                            api.updateSubmission(course.id, submissionId, url)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onDeleteSubmission = { submissionId ->
                                    scope.launch {
                                        try {
                                            api.deleteSubmission(course.id, submissionId)
                                            reload(showSpinner = false)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onCheckInQrClick = { showCheckInQr = true },
                            )
                        }

                        CourseTab.Students -> Column(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxWidth()
                                .verticalScroll(rememberScrollState()),
                        ) {
                            StudentsPanel(
                                sheet = current,
                                editable = editable,
                                onAdd = { fullName, number ->
                                    scope.launch {
                                        try {
                                            api.addStudent(course.id, fullName, number)
                                            reload()
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onUpdate = { studentId, fullName, number ->
                                    scope.launch {
                                        try {
                                            api.updateStudent(course.id, studentId, fullName, number)
                                            reload()
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onDelete = { studentId ->
                                    scope.launch {
                                        try {
                                            api.deleteStudent(course.id, studentId)
                                            reload()
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onStats = { studentId ->
                                    scope.launch {
                                        try {
                                            stats = api.getStudentStats(course.id, studentId)
                                        } catch (e: Exception) {
                                            error = e.message
                                        }
                                    }
                                },
                                onOpenPublicStudent = { studentUid ->
                                    onOpenPublicStudent(course.publicToken, studentUid)
                                },
                            )
                        }

                        CourseTab.Analytics -> Column(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxWidth()
                                .verticalScroll(rememberScrollState()),
                        ) {
                            val a = analytics
                            if (a == null) LoadingBox()
                            else AnalyticsPanel(a) { studentId ->
                                scope.launch {
                                    try {
                                        stats = api.getStudentStats(course.id, studentId)
                                    } catch (e: Exception) {
                                        error = e.message
                                    }
                                }
                            }
                        }
                    }
                }
            }
            }
        }

        stats?.let { s ->
            StudentStatsDialog(
                stats = s,
                isAdmin = isAdmin,
                showSubmissionLinks = true,
                publicToken = course.publicToken,
                onClose = { stats = null },
            )
        }

        if (showCheckInQr) {
            CheckInQrDialog(
                api = api,
                courseId = course.id,
                sessions = sheet?.sessions.orEmpty(),
                onDismiss = {
                    showCheckInQr = false
                    reload(showSpinner = false)
                },
                onError = { error = it },
            )
        }

        if (showFormsSettings && isAdmin) {
            CourseFormsSettingsDialog(
                settings = formsSettings,
                saving = savingFormsSettings,
                onDismiss = { showFormsSettings = false },
                onSave = { baseUrl, bearer ->
                    scope.launch {
                        savingFormsSettings = true
                        error = null
                        try {
                            formsSettings = api.updateCourseFormsSettings(course.id, baseUrl, bearer)
                            showFormsSettings = false
                        } catch (e: Exception) {
                            error = e.message
                        } finally {
                            savingFormsSettings = false
                        }
                    }
                },
            )
        }
    }
}
